import type { Artifact, ConfigurationFile, ServiceInstance } from '@/api/configuration';
import { listChangedServices, listDeployedArtifactHistory, listDeployedConfigFiles } from '@/api/configuration';
import { useArtifacts, useConfigFiles, useEnvironments, useServiceDefinitions, useServiceInstances } from '@/hooks/use-configuration';
import { useEnvironmentStore } from '@/stores/environment-store';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { EnvironmentSelector } from '@/components/shared/environment-selector';
// --- Types ---

interface ArtifactCurrentState {
  artifact: Artifact;
  version: string | null;
  deployedAt: string | null;
}

interface ConfigFileCurrentState {
  configFile: ConfigurationFile;
  version: string | null;
  deployedAt: string | null;
}

interface ServiceCurrentState {
  serviceInstance: ServiceInstance;
  lastAction: string | null;
  changedAt: string | null;
}

// --- Main Page ---

export function ProjectStatePage() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const key = projectKey ?? '';
  const { isLoading: envsLoading } = useEnvironments(key);
  const { data: artifacts = [], isLoading: artifactsLoading } = useArtifacts(key);
  const { data: configFiles = [], isLoading: configFilesLoading } = useConfigFiles(key!);

  const { selectedEnvironmentId: selectedEnv } = useEnvironmentStore();
  const { data: serviceInstances = [], isLoading: servicesLoading } = useServiceInstances(key, selectedEnv ?? '');
  const { data: serviceDefinitions = [] } = useServiceDefinitions(key);

  const [artifactStates, setArtifactStates] = useState<ArtifactCurrentState[]>([]);
  const [configFileStates, setConfigFileStates] = useState<ConfigFileCurrentState[]>([]);
  const [serviceStates, setServiceStates] = useState<ServiceCurrentState[]>([]);
  const [loadingState, setLoadingState] = useState(false);

  // Load current state when environment changes
  const loadCurrentState = useCallback(async () => {
    if (!selectedEnv || artifacts.length === 0) return;
    setLoadingState(true);

    try {
      // Fetch latest deployed version for each artifact
      const artifactResults = await Promise.all(
        artifacts.map(async (artifact): Promise<ArtifactCurrentState> => {
          try {
            const history = await listDeployedArtifactHistory(key, selectedEnv, artifact.id);
            const latest = history[0];
            return {
              artifact,
              version: latest?.version ?? null,
              deployedAt: latest?.deployedAt ?? null,
            };
          } catch {
            return { artifact, version: null, deployedAt: null };
          }
        })
      );
      setArtifactStates(artifactResults.sort((a, b) => a.artifact.name.localeCompare(b.artifact.name)));

      // Fetch latest deployed config files from the audit table
      try {
        const deployedConfigs = await listDeployedConfigFiles(key, selectedEnv);
        const configResults: ConfigFileCurrentState[] = configFiles.map((cf) => {
          const match = deployedConfigs.find((d) => d.configFileId === cf.id);
          return {
            configFile: cf,
            version: match?.version ?? null,
            deployedAt: match?.deployedAt ?? null,
          };
        });
        setConfigFileStates(configResults);
      } catch {
        setConfigFileStates(configFiles.map((cf) => ({ configFile: cf, version: null, deployedAt: null })));
      }

      // Resolve the display name for a service instance: prefer the linked
      // service definition's name, then the instance override name, then the id.
      const displayNameFor = (si: ServiceInstance) => {
        const def = serviceDefinitions.find((d) => d.id === si.serviceDefinitionId);
        return def?.name || si.serviceName || si.id;
      };
      const byDisplayName = (a: ServiceCurrentState, b: ServiceCurrentState) =>
        displayNameFor(a.serviceInstance).localeCompare(displayNameFor(b.serviceInstance));

      // Fetch latest service state changes from the audit table
      try {
        const changedServices = await listChangedServices(key, selectedEnv);
        const serviceResults: ServiceCurrentState[] = serviceInstances.map((si) => {
          const match = changedServices.find((cs) => cs.serviceInstanceId === si.id);
          return {
            serviceInstance: si,
            lastAction: match?.lastAction ?? null,
            changedAt: match?.changedAt ?? null,
          };
        });
        setServiceStates(serviceResults.sort(byDisplayName));
      } catch {
        setServiceStates(
          serviceInstances
            .map((si) => ({ serviceInstance: si, lastAction: null, changedAt: null }))
            .sort(byDisplayName)
        );
      }
    } finally {
      setLoadingState(false);
    }
  }, [selectedEnv, artifacts, configFiles, serviceInstances, serviceDefinitions, key]);

  useEffect(() => {
    if (selectedEnv && artifacts.length > 0) {
      loadCurrentState();
    }
  }, [selectedEnv, artifacts.length, configFiles.length, serviceInstances.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = envsLoading || artifactsLoading || configFilesLoading || servicesLoading;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.toLocaleDateString()} at ${d.toLocaleTimeString()}`;
  };

  const getActionBadgeClasses = (action: string | null) => {
    switch (action) {
      case 'START':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'STOP':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'RESTART':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <main id="main-content" className="mx-auto max-w-5xl">
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-content">Current State</h1>
        <p className="mt-1 text-sm text-content-secondary">
          View current state of your environments.
        </p>
      </header>

      {/* Environment selector */}
      <div className="mb-4">
        <EnvironmentSelector projectKey={key} />
      </div>

      {/* Page content */}
          <div className="space-y-6">
      {!selectedEnv && !loading && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center">
          <p className="text-sm text-content-tertiary">No environments available.</p>
        </div>
      )}

      {selectedEnv && !loadingState && (
        <>
          {/* Deployed Artifacts */}
          <section>
            <h3 className="text-sm font-semibold text-content mb-3">Artifacts</h3>
            {artifactStates.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-4 text-center">
                <p className="text-xs text-content-tertiary">No artifacts configured for this project.</p>
              </div>
            )}
            {artifactStates.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-raised border-b border-border">
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">Artifact</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">Version</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">Deployed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifactStates.map((state) => (
                      <tr key={state.artifact.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-content">{state.artifact.name}</span>
                          {state.artifact.downloadType && (
                            <span className="ml-2 text-xs text-content-tertiary">({state.artifact.downloadType})</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {state.version ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              {state.version}
                            </span>
                          ) : (
                            <span className="text-xs text-content-tertiary italic">Not deployed</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-content-secondary">
                          {formatDate(state.deployedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Services */}
          {serviceInstances.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-content mb-3">Services</h3>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-raised border-b border-border">
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">Service</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">Last Action</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">Changed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceStates.map((state) => (
                      <tr key={state.serviceInstance.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-content">
                            {serviceDefinitions.find((d) => d.id === state.serviceInstance.serviceDefinitionId)?.name
                              || state.serviceInstance.serviceName
                              || state.serviceInstance.id}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          {state.lastAction ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionBadgeClasses(state.lastAction)}`}>
                              {state.lastAction}
                            </span>
                          ) : (
                            <span className="text-xs text-content-tertiary italic">No actions recorded</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-content-secondary">
                          {formatDate(state.changedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Config Files */}
          {configFiles.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-content mb-3">Configuration Files</h3>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-raised border-b border-border">
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">File</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">Version</th>
                      <th className="text-left px-4 py-2 text-xs font-medium text-content-secondary">Last Deployed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configFileStates.map((state) => (
                      <tr key={state.configFile.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-content">{state.configFile.name}</span>
                          {state.configFile.targetFilename && (
                            <span className="ml-2 text-xs text-content-tertiary font-mono">{state.configFile.targetFilename}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {state.version ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              {state.version}
                            </span>
                          ) : (
                            <span className="text-xs text-content-tertiary italic">Not deployed</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-content-secondary">
                          {formatDate(state.deployedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {selectedEnv && loadingState && (
        <div className="animate-pulse space-y-3">
          <div className="h-8 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-32 rounded-lg bg-gray-200 dark:bg-gray-700" />
          <div className="h-8 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-24 rounded-lg bg-gray-200 dark:bg-gray-700" />
          <div className="h-8 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-24 rounded-lg bg-gray-200 dark:bg-gray-700" />
        </div>
      )}
    </div>
      
    </main>
  );
}
